import React, { useMemo, useState } from "react";

import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import { ResponsiveRadar } from "@nivo/radar";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";

import palette from "../../../common/styles/palette";
import { AiCard, AiBlocoTitulo, AiPill } from "./aiSurface";
import { AiAnalysis } from "../../../services/sheai/SheAiChecklistTypes";
import { extrairDadosSoftSkills, formatarMinutosSegundos, SoftSkillsSttResult } from "./checklistMultimodalData";
import ChecklistSoftSkillsEvidence, { extractSoftSkillEvidence, matchesSoftSkillEvidence } from "./ChecklistSoftSkillsEvidence";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { seekMainVideo } from "../debriefing-checklist/timestampLinks";

interface ChecklistSoftSkillsSectionProps {
  analise?: AiAnalysis | null;
  sttResult?: SoftSkillsSttResult | null;
}

const ChecklistSoftSkillsSection: React.FC<ChecklistSoftSkillsSectionProps> = ({
  analise,
  sttResult,
}) => {
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>("todas");
  const [filtroTextoTranscript, setFiltroTextoTranscript] = useState<string>("");
  const [segmentoAtivoId, setSegmentoAtivoId] = useState<number | null>(null);
  const [abaVisao, setAbaVisao] = useState<number>(0);

  const dados = useMemo(() => extrairDadosSoftSkills(analise, sttResult), [analise, sttResult]);
  const evidencias = useMemo(() => extractSoftSkillEvidence(analise, dados.timelineFala), [analise, dados.timelineFala]);

  const palavrasFiltradas =
    categoriaFiltro === "todas"
      ? dados.nuvemPalavras
      : dados.nuvemPalavras.filter((p) => p.categoria === categoriaFiltro);

  const termoBusca = filtroTextoTranscript.trim().toLocaleLowerCase();
  const timelineFiltrada = dados.timelineFala.filter((item) => !termoBusca || item.texto.toLocaleLowerCase().includes(termoBusca));
  const evidenciasFiltradas = useMemo(
    () => evidencias.filter((moment) => matchesSoftSkillEvidence(moment, termoBusca)),
    [evidencias, termoBusca]
  );

  /** Pula o vídeo para um segundo específico e dá play */
  const pularParaSegundo = (segundos: number, segmentoId?: number) => {
    seekMainVideo(formatarMinutosSegundos(segundos));
    if (segmentoId !== undefined) {
      setSegmentoAtivoId(segmentoId);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 3, mt: 1 }}>
      {/* Banner Principal de Soft Skills & Graph Agents */}
      <AiCard sx={{ borderLeft: `4px solid ${palette.brand}` }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <PsychologyOutlinedIcon sx={{ color: palette.brand, fontSize: 36, mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: palette.brandDark }}>
                Avaliação Multimodal de Soft Skills & Análise Comportamental
              </Typography>
              <AiPill label="Análise multimodal por IA" tom="marca" />
              <AiPill label="Expressão, postura e comunicação" tom="sucesso" />
            </Box>
            <Typography variant="body2" sx={{ color: palette.textMuted }}>
              Consulte a transcrição e as evidências de soft skills identificadas na gravação.
            </Typography>
          </Box>
        </Box>
      </AiCard>

      {/* ── LINHA DO TEMPO E VISÕES: utilizam o player compartilhado de Dados IA ── */}
      <AiCard>
        {/* 2. Linha do Tempo de Áudio/Voz por Pessoa (Diarização Temporal Real) */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TimelineOutlinedIcon sx={{ color: palette.brand, fontSize: 22 }} />
            <AiBlocoTitulo>Linha do Tempo de Diarização de Voz (Turnos de Fala vs. Tempo)</AiBlocoTitulo>
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: palette.brand }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: palette.brandDark }}>
                Estudante ({dados.pctEstudante}%)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: palette.lilacMid }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: palette.brandDark }}>
                Paciente / Atriz ({dados.pctPaciente}%)
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
          Mapeamento das emissões vocais reais de 00:00 a {formatarMinutosSegundos(dados.tempoTotal)}. <strong>Clique em qualquer bloco</strong> para pular o vídeo diretamente para a fala.
        </Typography>

        {/* Barra Visual de Linha do Tempo Multitrack */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "10px",
            backgroundColor: palette.brandWash,
            border: `1px solid ${palette.lilacMid}44`,
          }}
        >
          {/* Track 1: Estudante */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RecordVoiceOverOutlinedIcon sx={{ fontSize: 18, color: palette.brand }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: palette.brandDark }}>
                  🧑‍⚕️ Estudante (Turnos de Fala)
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: palette.textMuted, fontWeight: 600 }}>
                {formatarMinutosSegundos(dados.tempoEstudante)} de fala ativa
              </Typography>
            </Box>
            <Box sx={{ width: "100%", height: 32, backgroundColor: "rgba(139, 92, 246, 0.12)", borderRadius: "6px", position: "relative", overflow: "hidden" }}>
              {dados.timelineFala
                .filter((t) => t.speaker === "estudante")
                .map((seg) => {
                  const leftPct = (seg.start / dados.tempoTotal) * 100;
                  const widthPct = Math.max(3.5, ((seg.end - seg.start) / dados.tempoTotal) * 100);
                  const isHoveredOrActive = segmentoAtivoId === seg.id;

                  return (
                    <Tooltip
                      key={seg.id}
                      title={`${formatarMinutosSegundos(seg.start)} - ${formatarMinutosSegundos(seg.end)}: "${seg.texto}"`}
                      arrow
                    >
                      <Box
                        onClick={() => pularParaSegundo(seg.start, seg.id)}
                        onMouseEnter={() => setSegmentoAtivoId(seg.id)}
                        onMouseLeave={() => setSegmentoAtivoId(null)}
                        sx={{
                          position: "absolute",
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          height: "100%",
                          backgroundColor: isHoveredOrActive ? palette.brandDark : palette.brand,
                          border: isHoveredOrActive ? "2px solid #FFFFFF" : "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          transform: isHoveredOrActive ? "scaleY(1.2)" : "none",
                          zIndex: isHoveredOrActive ? 5 : 1,
                        }}
                      />

                    </Tooltip>

                  );
                })}
            </Box>
          </Box>

          {/* Track 2: Paciente */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ForumOutlinedIcon sx={{ fontSize: 18, color: palette.lilacMid }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: palette.brandDark }}>
                  👤 Paciente / Atriz (Turnos de Fala)
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: palette.textMuted, fontWeight: 600 }}>
                {formatarMinutosSegundos(dados.tempoPaciente)} de fala ativa
              </Typography>
            </Box>
            <Box sx={{ width: "100%", height: 32, backgroundColor: "rgba(167, 139, 250, 0.15)", borderRadius: "6px", position: "relative", overflow: "hidden" }}>
              {dados.timelineFala
                .filter((t) => t.speaker === "paciente")
                .map((seg) => {
                  const leftPct = (seg.start / dados.tempoTotal) * 100;
                  const widthPct = Math.max(3.5, ((seg.end - seg.start) / dados.tempoTotal) * 100);
                  const isHoveredOrActive = segmentoAtivoId === seg.id;

                  return (
                    <Tooltip
                      key={seg.id}
                      title={`${formatarMinutosSegundos(seg.start)} - ${formatarMinutosSegundos(seg.end)}: "${seg.texto}"`}
                      arrow
                    >
                      <Box
                        onClick={() => pularParaSegundo(seg.start, seg.id)}
                        onMouseEnter={() => setSegmentoAtivoId(seg.id)}
                        onMouseLeave={() => setSegmentoAtivoId(null)}
                        sx={{
                          position: "absolute",
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          height: "100%",
                          backgroundColor: isHoveredOrActive ? palette.brand : palette.lilacMid,
                          border: isHoveredOrActive ? "2px solid #FFFFFF" : "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          transform: isHoveredOrActive ? "scaleY(1.2)" : "none",
                          zIndex: isHoveredOrActive ? 5 : 1,
                        }}
                      />
                    </Tooltip>
                  );
                })}
            </Box>
          </Box>

          {/* Marcadores de Tempo da Régua */}
          <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1, borderTop: `1px solid ${palette.lilacMid}33` }}>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>00:00</Typography>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>{formatarMinutosSegundos(dados.tempoTotal / 4)}</Typography>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>{formatarMinutosSegundos(dados.tempoTotal / 2)}</Typography>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>{formatarMinutosSegundos((dados.tempoTotal * 3) / 4)}</Typography>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>{formatarMinutosSegundos(dados.tempoTotal)} (Término)</Typography>
          </Box>
        </Paper>

        {/* Transcrição completa e consulta de evidências de soft skills. */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
            <Tabs
              value={abaVisao}
              onChange={(_, v) => setAbaVisao(v)}
              sx={{
                "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: "0.95rem" },
              }}
            >
              <Tab
                icon={<TextSnippetOutlinedIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Transcrição na Íntegra (Áudio Puro)"
              />
              <Tab
                icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Evidências de soft skills"
              />
            </Tabs>

            <TextField
              size="small"
              placeholder="Buscar palavra ou termo..."
              value={filtroTextoTranscript}
              onChange={(e) => setFiltroTextoTranscript(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: palette.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240, "& input": { fontSize: "12px", py: 0.6 } }}
            />
          </Box>

          {/* VISÃO 1: Transcrição na Íntegra (Áudio Puro - Limpo, sem poluição visual) */}
          {abaVisao === 0 && (
            <Paper
              elevation={0}
              sx={{
                maxHeight: 480,
                overflowY: "auto",
                p: 2,
                borderRadius: "10px",
                border: `1px solid ${palette.border}`,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box sx={{ mb: 1.5, pb: 1, borderBottom: `1px solid ${palette.border}` }}>
                <Typography variant="caption" sx={{ color: palette.textMuted, fontWeight: 600 }}>
                  💡 Clique no horário ou na fala para posicionar o vídeo exatamente no início da pronúncia.
                </Typography>
              </Box>

              {timelineFiltrada.length === 0 && (
                <Typography variant="body2" sx={{ color: palette.textMuted, py: 2 }}>
                  Transcrição literal ainda não disponível para esta gravação.
                </Typography>
              )}
              {timelineFiltrada.map((item) => {
                const isHovered = segmentoAtivoId === item.id;
                const isEstudante = item.speaker === "estudante";

                return (
                  <Box
                    id={`transcript-item-${item.id}`}
                    key={item.id}
                    onClick={() => pularParaSegundo(item.start, item.id)}
                    onMouseEnter={() => setSegmentoAtivoId(item.id)}
                    onMouseLeave={() => setSegmentoAtivoId(null)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 1.5,
                      mb: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      backgroundColor: isHovered
                        ? `${palette.brand}15`
                        : isEstudante
                        ? "rgba(245, 243, 255, 0.6)"
                        : "rgba(254, 242, 242, 0.6)",
                      borderLeft: `4px solid ${isEstudante ? palette.brand : palette.lilacMid}`,
                      "&:hover": {
                        backgroundColor: `${palette.brand}15`,
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 140, display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Chip
                        label={isEstudante ? "🧑‍⚕️ Estudante" : "👤 Paciente"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "11px",
                          backgroundColor: isEstudante ? `${palette.brand}20` : `${palette.lilacMid}30`,
                          color: palette.brandDark,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: palette.brand,
                          fontFamily: "monospace",
                        }}
                      >
                        {formatarMinutosSegundos(item.start)} - {formatarMinutosSegundos(item.end)}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        color: palette.text,
                        lineHeight: 1.6,
                        fontSize: "0.95rem",
                        fontWeight: isEstudante ? 600 : 500,
                      }}
                    >
                      {`"${item.texto}"`}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          )}

          {abaVisao === 1 && <ChecklistSoftSkillsEvidence moments={evidenciasFiltradas} />}
        </Box>
      </AiCard>

      {!analise?.soft_skills || !dados.pareceres ? (
        <AiCard>
          <Typography variant="body2" sx={{ color: palette.textMuted }}>
            Leituras de Soft Skills ainda não disponíveis para esta gravação. Os gráficos e pareceres aparecem quando a análise as produzir.
          </Typography>
        </AiCard>
      ) : (
      <>
      {/* Grid com os 4 Gráficos Principais */}
      <Grid container spacing={2.5}>
        {/* 1. Gráfico de Teia de Sentimentos */}
        <Grid item xs={12} md={6}>
          <AiCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <FaceRetouchingNaturalIcon sx={{ color: palette.brand, fontSize: 20 }} />
              <AiBlocoTitulo>Gráfico de Teia Comportamental</AiBlocoTitulo>
            </Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
              Mapeamento radar dos aspectos comportamentais.
            </Typography>
            <Box sx={{ height: 320, width: "100%", position: "relative" }}>
              <ResponsiveRadar
                data={dados.radarSentimentos}
                keys={["score", "referencia"]}
                indexBy="atributo"
                maxValue={100}
                margin={{ top: 30, right: 60, bottom: 40, left: 60 }}
                curve="linearClosed"
                borderWidth={2}
                borderColor={{ from: "color" }}
                gridLevels={4}
                gridShape="circular"
                gridLabelOffset={16}
                enableDots={true}
                dotSize={8}
                dotColor={{ theme: "background" }}
                dotBorderWidth={2}
                dotBorderColor={{ from: "color" }}
                colors={[palette.brand, "#94A3B8"]}
                fillOpacity={0.25}
                blendMode="multiply"
                animate={true}
                motionConfig="gentle"
                legends={[
                  {
                    anchor: "top-right",
                    direction: "column",
                    translateX: 40,
                    translateY: -20,
                    itemWidth: 80,
                    itemHeight: 20,
                    itemTextColor: palette.textMuted,
                    symbolSize: 12,
                    symbolShape: "circle",

                  },

                ]}
              />
            </Box>
          </AiCard>
        </Grid>

        {/* 2. Gráfico de Tempo de Fala das Pessoas */}
        <Grid item xs={12} md={6}>
          <AiCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <GraphicEqOutlinedIcon sx={{ color: palette.brand, fontSize: 20 }} />
              <AiBlocoTitulo>Tempo de Fala das Pessoas</AiBlocoTitulo>
            </Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
              Duração acumulada da fala por participante durante a consulta (segundos).
            </Typography>
            <Box sx={{ height: 320, width: "100%" }}>
              <ResponsiveBar
                data={dados.tempoFala}
                keys={["segundos"]}
                indexBy="participante"
                margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
                padding={0.35}
                valueScale={{ type: "linear" }}
                indexScale={{ type: "band", round: true }}
                colors={({ data }) => data.cor}
                borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Participante",
                  legendPosition: "middle",
                  legendOffset: 38,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Duração (segundos)",
                  legendPosition: "middle",
                  legendOffset: -50,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                label={({ data }) => `${data.label}`}
                labelTextColor="#FFFFFF"
                animate={true}
                motionConfig="gentle"
              />
            </Box>
            <Box sx={{ mt: "auto", pt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Tempo Total: ${formatarMinutosSegundos(dados.tempoTotal)}`}
                size="small"
                sx={{ backgroundColor: `${palette.brand}15`, color: palette.brandDark, fontWeight: 600 }}
              />
              <Chip
                label={`Estudante: ${formatarMinutosSegundos(dados.tempoEstudante)} (${dados.pctEstudante}%)`}
                size="small"
                sx={{ backgroundColor: `${palette.brand}25`, color: palette.brandDark, fontWeight: 600 }}
              />
            </Box>
          </AiCard>
        </Grid>

        {/* 3. Gráfico de Rosca da Participação do Tempo de Fala */}
        <Grid item xs={12} md={6}>
          <AiCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <PieChartOutlinedIcon sx={{ color: palette.brand, fontSize: 20 }} />
              <AiBlocoTitulo>Participação no Tempo de Fala</AiBlocoTitulo>
            </Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
              Proporção percentual da condução verbal entre estudante e paciente.
            </Typography>
            <Box sx={{ height: 300, width: "100%" }}>
              <ResponsivePie
                data={dados.participacaoFala}
                margin={{ top: 20, right: 80, bottom: 40, left: 80 }}
                innerRadius={0.62}
                padAngle={2}
                cornerRadius={4}
                activeOuterRadiusOffset={8}
                colors={({ data }) => data.color}
                borderWidth={1}
                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor={palette.text}
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: "color" }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#FFFFFF"
                arcLabel={(d) => `${d.value}%`}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 35,
                    itemWidth: 100,
                    itemHeight: 18,
                    itemTextColor: palette.textMuted,
                    symbolSize: 12,
                    symbolShape: "circle",
                  },
                ]}
              />
            </Box>
            <Box sx={{ mt: "auto", pt: 1 }}>
              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                * Equilíbrio recomendado em OSCE: Estudante entre 65% e 75% da posse de fala.
              </Typography>
            </Box>
          </AiCard>
        </Grid>

        {/* 4. Gráfico de Nuvem de Palavras */}
        <Grid item xs={12} md={6}>
          <AiCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CloudQueueOutlinedIcon sx={{ color: palette.brand, fontSize: 20 }} />
                <AiBlocoTitulo>Nuvem de Palavras da Consulta</AiBlocoTitulo>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 1.5 }}>
              Termos e conceitos mais enfatizados na verbalização durante o atendimento.
            </Typography>

            {/* Filtro de categorias */}
            <Box sx={{ display: "flex", gap: 0.75, mb: 2, flexWrap: "wrap" }}>
              {[
                { key: "todas", label: "Todas" },
                { key: "clinica", label: "Clínica" },
                { key: "comunicacao", label: "Comunicação" },
                { key: "atitude", label: "Atitude & Ética" },
              ].map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  onClick={() => setCategoriaFiltro(f.key)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    backgroundColor:
                      categoriaFiltro === f.key ? palette.brand : `${palette.lilacMid}20`,
                    color: categoriaFiltro === f.key ? "#FFF" : palette.brandDark,
                    "&:hover": {
                      backgroundColor:
                        categoriaFiltro === f.key ? palette.brandDark : `${palette.lilacMid}40`,
                    },
                  }}
                />
              ))}
            </Box>

            {/* Nuvem interativa renderizada com pesos ponderados das palavras faladas */}
            {(() => {
              const pesos = palavrasFiltradas.map((p) => p.peso);
              const maxPeso = pesos.length > 0 ? Math.max(...pesos, 1) : 1;
              const minPeso = pesos.length > 0 ? Math.min(...pesos) : 1;
              const amplitude = maxPeso - minPeso;

              return (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#FFFFFF",
                    border: `1px solid ${palette.border}`,
                    boxShadow: palette.shadowSm,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",
                    gap: "10px 14px",
                    minHeight: 230,
                    my: "auto",
                  }}
                >
                  {palavrasFiltradas.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center", width: "100%" }}>
                      <Typography variant="body2" sx={{ color: palette.textMuted, fontWeight: 500 }}>
                        Nenhum termo verbalizado nesta categoria.
                      </Typography>
                      <Typography variant="caption" sx={{ color: palette.textMuted }}>
                        Selecione &quot;Todas&quot; ou verifique os outros filtros de soft skills.
                      </Typography>
                    </Box>
                  ) : (
                    palavrasFiltradas.map((item, idx) => {
                      // Normalização relativa entre 0 e 1 baseada na amplitude de ocorrências reais
                      const ratio = amplitude > 0 ? (item.peso - minPeso) / amplitude : 0.5;

                      // Escala de tamanho dinâmica de 14px (mínimo legível) até 30px (máximo destaque)
                      const fontSize = Math.round(14 + ratio * 16);
                      const isDestaque = amplitude > 0 ? ratio >= 0.75 : item.peso >= 3;
                      const isMedio = amplitude > 0 ? ratio >= 0.35 && ratio < 0.75 : item.peso === 2;
                      const isAtivo = filtroTextoTranscript.toLowerCase() === item.termo.toLowerCase();

                      const corTexto =
                        item.categoria === "clinica"
                          ? palette.brand
                          : item.categoria === "comunicacao"
                          ? "#2563EB"
                          : "#059669";

                      const bgCor =
                        item.categoria === "clinica"
                          ? "rgba(139, 92, 246, 0.12)"
                          : item.categoria === "comunicacao"
                          ? "rgba(37, 99, 235, 0.12)"
                          : "rgba(5, 150, 105, 0.12)";

                      return (
                        <Tooltip
                          key={idx}
                          title={`"${item.termo}" — ${item.peso} ${
                            item.peso === 1 ? "ocorrência" : "ocorrências"
                          } na consulta. Clique para ${
                            isAtivo ? "remover filtro do diálogo" : "localizar no diálogo"
                          }`}
                          arrow
                        >
                          <Box
                            onClick={() => {
                              setFiltroTextoTranscript(isAtivo ? "" : item.termo);
                            }}
                            sx={{
                              fontSize: `${fontSize}px`,
                              fontWeight: isDestaque ? 800 : isMedio ? 650 : 500,
                              color: isAtivo ? "#FFFFFF" : corTexto,
                              backgroundColor: isAtivo
                                ? corTexto
                                : isDestaque || isMedio
                                ? bgCor
                                : "transparent",
                              border: isAtivo
                                ? `2px solid ${corTexto}`
                                : isDestaque
                                ? `1px solid ${corTexto}40`
                                : "1px solid transparent",
                              px: isDestaque ? 1.5 : 1,
                              py: isDestaque ? 0.6 : 0.3,
                              borderRadius: "8px",
                              cursor: "pointer",
                              lineHeight: 1.2,
                              transition: "all 0.15s ease-in-out",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              userSelect: "none",
                              boxShadow: isAtivo ? `0 2px 8px ${corTexto}60` : "none",
                              "&:hover": {
                                transform: "scale(1.12)",
                                color: isAtivo ? "#FFFFFF" : palette.brandDark,
                                backgroundColor: isAtivo ? corTexto : `${palette.brand}20`,
                                zIndex: 2,
                              },
                            }}
                          >
                            <span>{item.termo}</span>
                            {(isDestaque || isMedio) && (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: "0.72em",
                                  fontWeight: 700,
                                  opacity: isAtivo ? 0.9 : 0.7,
                                  ml: 0.25,
                                }}
                              >
                                ({item.peso})
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })
                  )}
                </Paper>
              );
            })()}

            {/* Rodapé explicativo da proposta e status de filtro */}
            <Box
              sx={{
                mt: "auto",
                pt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              {filtroTextoTranscript && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: `${palette.brand}12`,
                    border: `1px solid ${palette.brand}40`,
                    borderRadius: "6px",
                    px: 1.25,
                    py: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: palette.brandDark, fontWeight: 600 }}>
                    Filtrando diálogo por: <strong>&ldquo;{filtroTextoTranscript}&rdquo;</strong>
                  </Typography>
                  <Chip
                    size="small"
                    label="Limpar filtro"
                    onClick={() => setFiltroTextoTranscript("")}
                    sx={{ height: 20, fontSize: "11px", cursor: "pointer" }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: palette.textMuted }}>
                  * Tamanho reflete a frequência das falas. Clique em um termo para filtrar o diálogo abaixo.
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Typography variant="caption" sx={{ color: palette.brand, fontWeight: 600 }}>
                    ● Clínica
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#2563EB", fontWeight: 600 }}>
                    ● Comunicação
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#059669", fontWeight: 600 }}>
                    ● Atitude & Ética
                  </Typography>
                </Box>
              </Box>
            </Box>
          </AiCard>
        </Grid>
      </Grid>

      {/* ── 3 Pilares Multimodais: Fala, Postura e Face ── */}
      <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: palette.brandDark, mt: 2 }}>
        Análise Multimodal Consolidada: Fala · Linguagem Corporal · Microexpressões Faciais
      </Typography>

      <Grid container spacing={2.5}>
        {/* Pilar 1: Análise Facial & Microexpressões */}
        <Grid item xs={12} md={4}>
          <AiCard sx={{ height: "100%", borderTop: `3px solid ${palette.brand}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <FaceRetouchingNaturalIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, color: palette.brandDark }}>
                {dados.pareceres.emocoes.titulo}
              </Typography>
            </Box>
            <Chip
              label={dados.pareceres.emocoes.notaAspecto}
              size="small"
              sx={{ mb: 1.5, backgroundColor: `${palette.brand}15`, color: palette.brandDark, fontWeight: 700 }}
            />
            <Typography variant="body2" sx={{ color: palette.text, mb: 2, lineHeight: 1.6 }}>
              {dados.pareceres.emocoes.resumo}
            </Typography>
            <Divider sx={{ my: 1.5, borderColor: `${palette.lilacMid}33` }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: palette.textMuted, display: "block", mb: 1 }}>
              SINAIS FACIAIS & VALÊNCIA:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, "& li": { fontSize: "0.85rem", color: palette.text, mb: 0.5 } }}>
              {dados.pareceres.emocoes.destaques.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </Box>
          </AiCard>
        </Grid>

        {/* Pilar 2: Linguagem Corporal & Postura */}
        <Grid item xs={12} md={4}>
          <AiCard sx={{ height: "100%", borderTop: `3px solid ${palette.lilacMid}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <AccessibilityNewOutlinedIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, color: palette.brandDark }}>
                {dados.pareceres.postura.titulo}
              </Typography>
            </Box>
            <Chip
              label={dados.pareceres.postura.notaAspecto}
              size="small"
              sx={{ mb: 1.5, backgroundColor: `${palette.brand}15`, color: palette.brandDark, fontWeight: 700 }}
            />
            <Typography variant="body2" sx={{ color: palette.text, mb: 2, lineHeight: 1.6 }}>
              {dados.pareceres.postura.resumo}
            </Typography>
            <Divider sx={{ my: 1.5, borderColor: `${palette.lilacMid}33` }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: palette.textMuted, display: "block", mb: 1 }}>
              PADRÕES CORPORAIS & GESTOS:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, "& li": { fontSize: "0.85rem", color: palette.text, mb: 0.5 } }}>
              {dados.pareceres.postura.padroes.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </Box>
          </AiCard>
        </Grid>

        {/* Pilar 3: Fala & Diálogo */}
        <Grid item xs={12} md={4}>
          <AiCard sx={{ height: "100%", borderTop: `3px solid #3B82F6` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <RecordVoiceOverOutlinedIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, color: palette.brandDark }}>
                {dados.pareceres.comunicacao.titulo}
              </Typography>
            </Box>
            <Chip
              label={dados.pareceres.comunicacao.notaAspecto}
              size="small"
              sx={{ mb: 1.5, backgroundColor: `${palette.brand}15`, color: palette.brandDark, fontWeight: 700 }}
            />
            <Typography variant="body2" sx={{ color: palette.text, mb: 2, lineHeight: 1.6 }}>
              {dados.pareceres.comunicacao.resumo}
            </Typography>
            <Divider sx={{ my: 1.5, borderColor: `${palette.lilacMid}33` }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: palette.textMuted, display: "block", mb: 1 }}>
              COMUNICAÇÃO VERBAL & RITMO:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, "& li": { fontSize: "0.85rem", color: palette.text, mb: 0.5 } }}>
              {dados.pareceres.comunicacao.indicadores.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </Box>
          </AiCard>
        </Grid>

        {/* Parecer Global Consolidado */}
        <Grid item xs={12}>
          <AiCard sx={{ backgroundColor: palette.brandWash, border: `1px solid ${palette.lilacMid}55` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <AutoAwesomeIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: palette.brandDark }}>
                {dados.pareceres.parecerGlobal.titulo}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: palette.text, mb: 2, lineHeight: 1.6 }}>
              {dados.pareceres.parecerGlobal.resumo}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "8px", backgroundColor: "#FFFFFF", height: "100%" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#065F46", mb: 1 }}>
                    ✓ Pontos Fortes Observados
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0, "& li": { fontSize: "0.85rem", color: palette.text, mb: 0.5 } }}>
                    {dados.pareceres.parecerGlobal.pontosFortes.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>

                <Paper elevation={0} sx={{ p: 2, borderRadius: "8px", backgroundColor: "#FFFFFF", height: "100%" }}>

                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#92400E", mb: 1 }}>
                    ⚠ Oportunidades de Desenvolvimento
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0, "& li": { fontSize: "0.85rem", color: palette.text, mb: 0.5 } }}>
                    {dados.pareceres.parecerGlobal.oportunidades.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${palette.lilacMid}33` }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: palette.brandDark }}>
                Síntese Pedagógica: {dados.pareceres.parecerGlobal.sintese}
              </Typography>
            </Box>
          </AiCard>
        </Grid>
      </Grid>
      </>
      )}
    </Box>
  );
};

export default ChecklistSoftSkillsSection;
